'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'motion/react';
import { Tv, PlayCircle, Shield, Zap, Globe, Smartphone, MonitorPlay, Cloud, Star, CheckCircle2, ChevronRight } from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 font-sans selection:bg-indigo-500/30 overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-600/20">
              <Tv className="text-white" size={20} />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              IPTV GO
            </span>
          </div>
          <div className="flex items-center gap-3 sm:gap-6">
            <Link href="/pricing" className="text-sm font-medium text-gray-300 hover:text-white transition-colors hidden sm:block">Pricing</Link>
            <Link href="/about" className="text-sm font-medium text-gray-300 hover:text-white transition-colors hidden md:block">About</Link>
            <a href="https://play.google.com/store/apps/details?id=com.nicxonsolutions.iptv_go" target="_blank" rel="noreferrer" className="hidden sm:flex px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white text-sm font-bold rounded-lg border border-gray-700 transition-all items-center gap-2">
              Download App
            </a>
            <Link href="/live" className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-lg shadow-lg shadow-indigo-600/20 transition-all flex items-center gap-2">
              Open Web Player
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center pt-20 pb-32 overflow-hidden">
          {/* Background Gradients */}
          <div className="absolute top-1/4 -left-64 w-96 h-96 bg-indigo-600/30 rounded-full blur-[128px] mix-blend-screen" />
          <div className="absolute bottom-1/4 -right-64 w-96 h-96 bg-purple-600/30 rounded-full blur-[128px] mix-blend-screen" />

          <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="flex-1 text-center lg:text-left"
              >
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-bold text-indigo-400 uppercase tracking-widest mb-6">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                  </span>
                  The #1 Next-Gen Streaming Platform
                </div>
                <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-8 leading-tight">
                  Stream Your World, <br />
                  <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Without Limits.
                  </span>
                </h1>
                <p className="text-lg md:text-xl text-gray-400 mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                  Experience television like never before. IPTV GO provides the most advanced, lightning-fast web player for M3U and M3U8 playlists. Enjoy pristine quality, seamless multi-screen view, and an ad-free interface tailored for ultimate entertainment.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                  <Link href="/live" className="w-full sm:w-auto px-8 py-4 bg-white text-gray-950 font-extrabold rounded-xl hover:bg-gray-100 transition-all shadow-xl shadow-white/10 flex items-center justify-center gap-2 group text-lg">
                    <PlayCircle className="group-hover:scale-110 transition-transform" />
                    Launch Web Player
                  </Link>
                  <a href="https://play.google.com/store/apps/details?id=com.nicxonsolutions.iptv_go" target="_blank" rel="noreferrer" className="w-full sm:w-auto px-8 py-4 bg-indigo-600/20 text-indigo-300 font-bold border border-indigo-500/30 rounded-xl hover:bg-indigo-600/30 transition-all flex items-center justify-center gap-2 text-lg">
                    <Smartphone className="group-hover:scale-110 transition-transform" />
                    Get Mobile App
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.2 }}
                className="flex-1 w-full max-w-2xl perspective-1000"
              >
                <div className="relative rounded-2xl border border-white/10 overflow-hidden shadow-2xl shadow-indigo-500/20 transform rotate-y-[-5deg] rotate-x-[5deg]">
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent z-10" />
                  <Image
                    src="/hero-mockup.png"
                    alt="IPTV GO Application Interface showing advanced layout"
                    width={1200}
                    height={800}
                    className="w-full h-auto object-cover opacity-90 hover:opacity-100 transition-opacity duration-500"
                    priority
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Long Text & SEO Section 1: Introduction to IPTV */}
        <section id="features" className="py-24 bg-gray-900/50 border-y border-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-6">
                What is Internet Protocol Television (IPTV)?
              </h2>
              <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                Discover the revolutionary technology that is reshaping the way we consume digital entertainment globally.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">The Evolution of Digital Broadcasting</h3>
                <div className="space-y-6 text-gray-300 leading-relaxed text-lg">
                  <p>
                    Internet Protocol Television (IPTV) represents a monumental shift in media consumption. Unlike traditional cable or satellite formats that rely on broadcast signals, IPTV delivers television content over Internet Protocol (IP) networks. This implies that your media is streamed directly via your internet connection, allowing for a highly interactive and personalized viewing experience. At IPTV GO, we harness this technology to provide an unparalleled streaming platform tailored to modern demands.
                  </p>
                  <p>
                    With the rising demand for on-the-go entertainment and global accessibility, traditional methods are rapidly becoming obsolete. IPTV provides the flexibility to watch what you want, when you want, without the constraints of rigid broadcasting schedules. By utilizing M3U and M3U8 playlists—industry-standard formats for multimedia streaming—users can curate their own custom channel lists containing thousands of live TV channels, movies, series, and special events from around the world.
                  </p>
                  <p>
                    The core advantage lies in packet-switched networking. Instead of sending all channels simultaneously (which consumes massive bandwidth), IPTV sends only the specific program requested by the user. When a new channel is selected, a fresh stream is initiated from the provider's server. This is where a high-performance player like IPTV GO becomes essential. Our ultra-fast web player instantly decodes these streams, utilizing advanced adaptive bitrate streaming like HLS (HTTP Live Streaming) to ensure smooth playback regardless of your connection speed.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 text-center hover:bg-gray-800 transition-colors">
                    <Globe className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
                    <h4 className="font-bold text-white mb-2">Global Access</h4>
                    <p className="text-sm text-gray-400">Overcome geographical limitations and access international content instantly.</p>
                  </div>
                  <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 text-center hover:bg-gray-800 transition-colors">
                    <Zap className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                    <h4 className="font-bold text-white mb-2">Ultra-Low Latency</h4>
                    <p className="text-sm text-gray-400">Experience true live television with minimal delay and buffering.</p>
                  </div>
                </div>
                <div className="space-y-6 mt-12">
                  <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 text-center hover:bg-gray-800 transition-colors">
                    <MonitorPlay className="w-12 h-12 text-pink-400 mx-auto mb-4" />
                    <h4 className="font-bold text-white mb-2">Cross-Platform</h4>
                    <p className="text-sm text-gray-400">Stream seamlessly on smart TVs, desktops, tablets, and smartphones.</p>
                  </div>
                  <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 text-center hover:bg-gray-800 transition-colors">
                    <Shield className="w-12 h-12 text-green-400 mx-auto mb-4" />
                    <h4 className="font-bold text-white mb-2">Secure & Private</h4>
                    <p className="text-sm text-gray-400">Your privacy is respected with client-side parsing and localized processing.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Feature Highlights Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-indigo-900/5 pointer-events-none" />
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1 flex flex-col justify-center">
                <h2 className="text-4xl font-bold mb-6">Engineered for the Modern Consumer</h2>
                <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                  Our application is not just a player; it's a comprehensive media center engineered to solve the common frustrations associated with streaming.
                </p>
                <Link href="/live" className="inline-flex items-center gap-2 text-indigo-400 font-bold hover:text-indigo-300 transition-colors">
                  Try it yourself <ChevronRight size={20} />
                </Link>
              </div>
              <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
                {[
                  {
                    icon: <Cloud className="min-w-8 text-blue-400" />,
                    title: "Cloud Synchronization",
                    description: "For premium members, we provide state-of-the-art cloud sync. Your favorite channels, custom playlists, and watch history are securely backed up to the cloud. Start watching on your laptop and seamlessly continue on your smart TV."
                  },
                  {
                    icon: <Tv className="min-w-8 text-indigo-400" />,
                    title: "Multi-Screen Mode",
                    description: "Can't decide between two crucial sports events or live news feeds? Our advanced multi-screen feature allows you to open up to four streams simultaneously. Monitor multiple streams with independent volume controls."
                  },
                  {
                    icon: <Star className="min-w-8 text-yellow-400" />,
                    title: "Premium Aesthetics",
                    description: "Built with the latest in web design technologies, our interface boasts a dark-mode optimized, cinematic layout that highlights your content. Navigation is intuitive, feeling native to your device whether using a mouse or remote."
                  },
                  {
                    icon: <Smartphone className="min-w-8 text-emerald-400" />,
                    title: "Mobile Optimization",
                    description: "We understand that entertainment is mobile. Our responsive architecture ensures the web player adapts perfectly to mobile screens, offering touch-friendly controls, swipe gestures, and battery-optimized video processing."
                  }
                ].map((feature, i) => (
                  <div key={i} className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 p-8 rounded-2xl hover:bg-gray-800 transition-all duration-300 hover:scale-[1.02] shadow-xl shadow-black/50">
                    <div className="mb-6 bg-gray-800 w-16 h-16 rounded-2xl flex items-center justify-center">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                    <p className="text-gray-400 leading-relaxed text-sm">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Mobile App & Ecosystem Section */}
        <section className="py-32 bg-gray-900 border-y border-white/5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-indigo-600/10 rounded-full blur-[120px] mix-blend-screen pointer-events-none" />
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="flex-1 w-full"
              >
                <div className="relative">
                  <Image
                    src="/mobile-mockup.png"
                    alt="IPTV GO Mobile App Interface"
                    width={800}
                    height={800}
                    className="w-full rounded-2xl max-w-sm mx-auto h-auto object-cover drop-shadow-2xl hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute -bottom-10 -right-6 md:-right-16 hidden md:block w-2/3">
                    <Image
                      src="/multi-device-mockup.png"
                      alt="IPTV GO Multi Device Syncing"
                      width={600}
                      height={400}
                      className="w-full h-auto object-cover rounded-2xl border border-white/10 shadow-2xl shadow-indigo-500/20 hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="flex-1 text-center lg:text-left mt-16 lg:mt-0"
              >
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-xs font-bold text-indigo-400 uppercase tracking-widest mb-6">
                  <Smartphone size={14} /> Available on Android
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                  Take Your TV <br className="hidden md:block" />
                  <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">Everywhere You Go</span>
                </h2>
                <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                  Our dedicated mobile application brings the full power of IPTV GO to your pocket. Designed specifically for smartphones and tablets, it offers a native, buttery-smooth experience that web browsers alone simply cannot match on mobile devices.
                </p>

                <ul className="space-y-6 mb-10 text-left">
                  <li className="flex items-start gap-4">
                    <div className="bg-indigo-600/20 p-2 rounded-lg mt-1">
                      <CheckCircle2 className="text-indigo-400 w-5 h-5" />
                    </div>
                    <div>
                      <strong className="text-white block mb-1">Seamless Cloud Sync</strong>
                      <span className="text-gray-400 text-sm leading-relaxed block">Start watching on your phone during your commute, and resume exactly where you left off on the web player at home.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="bg-indigo-600/20 p-2 rounded-lg mt-1">
                      <CheckCircle2 className="text-indigo-400 w-5 h-5" />
                    </div>
                    <div>
                      <strong className="text-white block mb-1">Native Performance</strong>
                      <span className="text-gray-400 text-sm leading-relaxed block">Enjoy hardware-accelerated video decoding optimized to preserve your device's battery life.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="bg-indigo-600/20 p-2 rounded-lg mt-1">
                      <CheckCircle2 className="text-indigo-400 w-5 h-5" />
                    </div>
                    <div>
                      <strong className="text-white block mb-1">Picture-in-Picture</strong>
                      <span className="text-gray-400 text-sm leading-relaxed block">Keep watching while you text, browse the web, or use other apps with native PiP support.</span>
                    </div>
                  </li>
                </ul>

                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                  <a href="https://play.google.com/store/apps/details?id=com.nicxonsolutions.iptv_go" target="_blank" rel="noreferrer" className="w-full sm:w-auto px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-lg transition-all shadow-xl shadow-indigo-600/30 flex items-center justify-center gap-3">
                    <Smartphone className="w-5 h-5" />
                    Google Play Store
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Long Text & SEO Section 2: Why M3U and Custom Playlists Matter */}
        <section className="py-24 bg-gray-950">
          <div className="max-w-4xl mx-auto px-6">
            <div className="prose prose-invert prose-lg max-w-none">
              <h2 className="text-3xl md:text-5xl font-bold mb-8 text-center bg-gradient-to-br from-indigo-400 to-purple-500 bg-clip-text text-transparent">
                Unleashing the Power of Custom Playlists
              </h2>

              <div className="space-y-8 text-gray-300">
                <p>
                  One of the most defining characteristics of modern IPTV services is the use of <strong>M3U (MP3 URL)</strong> and <strong>M3U8</strong> (UTF-8 encoded) playlists. Originally designed for simple audio files, these extensible playlist formats have become the de facto standard for structuring and organizing live video streams. But why does this matter to the everyday user? The answer lies in complete customization and control over the media landscape.
                </p>
                <p>
                  A typical traditional cable subscription bundles hundreds of channels, most of which go unwatched, forcing consumers to pay for "bloatware" television. By utilizing an M3U playlist through IPTV GO, you dictate exactly what appears on your screen. You can import community-driven lists, premium subscription URLs provided by verified broadcasters, or even compile your own personal list of specialized programming. This decentralized approach demystifies broadcasting, putting the power directly into the hands of the audience.
                </p>

                <div className="bg-indigo-900/20 border border-indigo-500/30 p-8 rounded-2xl my-10">
                  <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                    <CheckCircle2 className="text-indigo-400" />
                    How to Setup Your Playlist
                  </h3>
                  <p className="mb-4">Getting started with IPTV GO is remarkably straightforward:</p>
                  <ol className="list-decimal list-inside space-y-3 text-gray-400 ml-2">
                    <li>Obtain an M3U or M3U8 playlist URL from a legitimate provider or open-source community.</li>
                    <li>Navigate to the IPTV GO Dashboard.</li>
                    <li>Click on "Add Playlist" and paste your URL.</li>
                    <li>Our fast, client-side parser will instantly categorize hundreds of channels by group, country, and quality.</li>
                  </ol>
                </div>

                <p>
                  We have heavily optimized our <strong>playlist parsing engine</strong> to handle massive files. Some master playlists contain upwards of 50,000 live streams and thousands of Video On Demand (VOD) titles. Older or poorly optimized players will crash, lag, or consume vast amounts of memory when attempting to read such data. IPTV GO, built on modern React architecture, virtualizes these lists. This means whether you have 100 or 100,000 channels, scrolling remains buttery smooth and search functionality is instantaneous.
                </p>

                <h3 className="text-2xl font-bold text-white mt-12 mb-6">The Technical Backbone: HLS Streaming</h3>
                <p>
                  Behind every high-quality live stream is a robust protocol. IPTV GO utilizes HTTP Live Streaming (HLS), an adaptive bitrate protocol originally developed by Apple. HLS breaks the overall stream into a sequence of small HTTP-based file downloads, each loading one short chunk of the overall transport stream. As the stream plays, the client may select from a number of different alternate streams containing the same material encoded at various data rates.
                </p>
                <p>
                  If you start watching a sports game and your local network slows down, our player will automatically detect the drop in bandwidth and seamlessly downgrade the visual quality to prevent buffering stops. Once the network stabilizes, the quality scales back up to glorious HD or 4K. This dynamic adaptation is crucial for maintaining viewer immersion and preventing the dreaded loading spinner. Our dedication to integrating the finest HLS.js configurations ensures maximum compatibility across all major web browsers including Chrome, Safari, Firefox, and Edge.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section for SEO and AdSense */}
        <section className="py-24 bg-gray-900 border-t border-white/5">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-white">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {[
                { 
                  q: "Does IPTV GO provide live TV channels or content?", 
                  a: "No. IPTV GO is strictly a media player application. We do not provide, host, or sell any audio-visual content, live streams, or M3U playlists. Users must supply their own legally acquired content from third-party service providers."
                },
                { 
                  q: "Is it safe and legal to use IPTV GO?", 
                  a: "Yes, using a media player like IPTV GO is completely legal, much like using a web browser or a local media player. However, it is your responsibility to ensure that the playlists and content you load into the player are legally obtained and do not violate any copyright laws."
                },
                { 
                  q: "What types of playlists are supported?", 
                  a: "Our advanced web player fully supports standard M3U and M3U8 (UTF-8 encoded) playlist formats. It seamlessly processes URLs provided by your content provider or local playlist files uploaded directly from your device."
                },
                { 
                  q: "Can I use IPTV GO on multiple devices?", 
                  a: "Absolutely! Our player is completely web-based, meaning it works flawlessly across desktop computers, tablets, smartphones, and modern smart TVs. For the best experience on mobile and tablet, you can also download our dedicated Android application."
                },
                { 
                  q: "Is the web player completely free?", 
                  a: "Yes! The core functionality of IPTV GO is free to use and supported by unobtrusive advertisements. For users looking for an ad-free experience, multi-screen viewing, and advanced features, we offer highly affordable Premium subscription plans."
                }
              ].map((faq, idx) => (
                <div key={idx} className="bg-gray-800/50 border border-gray-700/50 hover:border-gray-600 transition-colors rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-white mb-3">{faq.q}</h3>
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-24 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0 bg-gradient-to-b from-indigo-900/20 to-gray-950 pointer-events-none" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-64 bg-indigo-600/30 blur-[120px] rounded-full" />

          <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-8">
              Ready to Upgrade Your <br className="hidden md:block" /> Entertainment Experience?
            </h2>
            <p className="text-xl text-indigo-200 mb-10 max-w-2xl mx-auto leading-relaxed">
              Join thousands of users who have already switched to the most intuitive, fast, and gorgeous IPTV web player on the internet. Start for free today. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link href="/live" className="px-10 py-5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-lg transition-all shadow-xl shadow-indigo-600/30 flex items-center justify-center gap-3 transform hover:-translate-y-1">
                <PlayCircle />
                Start Playing Free
              </Link>
              <Link href="/pricing" className="px-10 py-5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-bold text-lg transition-all border border-gray-700 flex items-center justify-center gap-3">
                <Star className="text-yellow-400" />
                View Premium Plans
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 bg-gray-950 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <Tv className="text-white" size={16} />
                </div>
                <span className="text-xl font-bold text-white">IPTV GO</span>
              </div>
              <p className="text-gray-400 max-w-sm mb-6 leading-relaxed text-sm">
                A premium, lightning-fast web player designed for optimal streaming of M3U and M3U8 playlists. Experience high-definition broadcasting globally.
              </p>
            </div>

            <div>
              <h4 className="text-white font-bold mb-6">Product</h4>
              <ul className="space-y-4">
                <li><Link href="/live" className="text-gray-400 hover:text-white transition-colors text-sm">Web Player</Link></li>
                <li><Link href="/pricing" className="text-gray-400 hover:text-white transition-colors text-sm">Premium Features</Link></li>
                <li><Link href="/about" className="text-gray-400 hover:text-white transition-colors text-sm">About Us</Link></li>
                <li><a href="https://play.google.com/store/apps/details?id=com.nicxonsolutions.iptv_go" target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm">Android App</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-6">Legal</h4>
              <ul className="space-y-4">
                <li><Link href="/terms" className="text-gray-400 hover:text-white transition-colors text-sm">Terms of Service</Link></li>
                <li><Link href="/privacy" className="text-gray-400 hover:text-white transition-colors text-sm">Privacy Policy</Link></li>
                <li><Link href="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">Contact Support</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} IPTV GO Platform. All rights reserved. Not affiliated with any content providers.
            </p>
            <p className="text-gray-600 text-xs text-center md:text-right max-w-xl">
              Disclaimer: IPTV GO is a playlist player only. We do not provide, host, or disseminate any audio-visual materials. Users must parse their own legally obtained M3U/M3U8 playlists to utilize this software.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
